import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis, restrictToParentElement } from "@dnd-kit/modifiers";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Link from "next/link";
import Router from "next/router";
import { useEffect, useMemo, useState } from "react";
import { dasherize } from "../utils/dom";
import { LS_Get, LS_Set } from "../utils/local-storage";

/**
 * The left navigation rail shared by the account area and the dash.
 *
 * It was written for /account and then wanted verbatim for /dash, so the markup,
 * the mobile drawer and the styling live here once. Callers supply what actually
 * differs: which items, what the badges count, and the optional call-to-action
 * pinned above the list.
 *
 * On desktop it is a fixed left column and the page content is inset to clear
 * it. Below $mobile-width the same column becomes a left slide-out drawer
 * toggled by a hamburger docked in the header, with a dimming backdrop.
 *
 * Pass `reorderKey` and the list becomes drag-sortable, with the order kept in
 * localStorage under that key so each person's rail stays how they left it.
 */

export type RailItem = {
  label: string;
  href: string;
  /** A path under /images for <img>, or a Lucide icon name — see `renderIcon`. */
  icon: string;
};

export type SideRailProps = {
  /** Current route, used to mark the active item and close the mobile drawer. */
  pathname: string;
  items: RailItem[];
  /** Route prefix an item must share to be considered active (e.g. "/account"). */
  basePath: string;
  /** Accessible name for the <nav>, and the stem of every data-testid. */
  name: string;
  testIdPrefix: string;
  /** Draws the item's icon. Lets /account pass <Img> and /dash pass Lucide. */
  renderIcon: (icon: string, label: string) => React.ReactNode;
  /** Unread/attention count for an item; 0 hides the badge. */
  badgeFor?: (href: string) => number;
  /** Optional filled button above the list (e.g. account's "Post Listing"). */
  cta?: { label: string; href: string; icon: string };
  /**
   * localStorage key holding this rail's saved item order. Supplying it turns on
   * drag-to-reorder; leaving it off keeps the rail a plain, static list.
   */
  reorderKey?: string;
};

function isActive(pathname: string, href: string, basePath: string): boolean {
  if (!href.startsWith(basePath)) return false;
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * Reorders `items` to match a saved list of hrefs.
 *
 * The saved order is deliberately not authoritative: hrefs that no longer exist
 * are dropped, and items the saved order has never seen (a nav entry added after
 * someone last dragged their rail) are appended in their shipped order rather
 * than vanishing. A missing or malformed saved order leaves `items` untouched.
 */
export function applyStoredOrder(items: RailItem[], stored: unknown): RailItem[] {
  if (!Array.isArray(stored)) return items;
  const byHref = new Map(items.map((i) => [i.href, i]));
  const ordered: RailItem[] = [];
  for (const href of stored) {
    const item = typeof href === "string" ? byHref.get(href) : undefined;
    if (item) {
      ordered.push(item);
      byHref.delete(href);
    }
  }
  // Whatever the saved order didn't mention keeps its shipped position, at the end.
  return [...ordered, ...items.filter((i) => byHref.has(i.href))];
}

/**
 * The href order produced by dropping `activeHref` onto `overHref`'s slot.
 *
 * Returns null when the drag changes nothing — dropped on itself, or on an item
 * this rail doesn't have — so the caller can skip the write to localStorage.
 */
export function reorderForDrag(
  items: RailItem[],
  activeHref: string,
  overHref: string
): string[] | null {
  if (activeHref === overHref) return null;
  const from = items.findIndex((i) => i.href === activeHref);
  const to = items.findIndex((i) => i.href === overHref);
  if (from === -1 || to === -1) return null;
  const next = [...items];
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next.map((i) => i.href);
}

/** Six-dot grip, drawn inline so both rails get it without importing an icon set. */
function GripIcon() {
  return (
    <svg viewBox="0 0 10 16" width="10" height="16" fill="currentColor" aria-hidden="true">
      <circle cx="2" cy="3" r="1.4" />
      <circle cx="8" cy="3" r="1.4" />
      <circle cx="2" cy="8" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="2" cy="13" r="1.4" />
      <circle cx="8" cy="13" r="1.4" />
    </svg>
  );
}

type RailLinkProps = {
  item: RailItem;
  active: boolean;
  count: number;
  testIdPrefix: string;
  renderIcon: SideRailProps["renderIcon"];
  onNavigate: () => void;
};

function RailLink({ item, active, count, testIdPrefix, renderIcon, onNavigate }: RailLinkProps) {
  // Load the page's code while the pointer is on its way, so the click has
  // nothing left to download. Link prefetches on viewport only in production
  // builds; this also covers a rail scrolled out of view and keyboard focus.
  const prefetch = () => {
    try {
      void Router.prefetch(item.href).catch(() => undefined);
    } catch {
      // No router mounted (a test, a static render): nothing to warm.
    }
  };
  return (
    <Link
      href={item.href}
      className={`side-rail__item${active ? " side-rail__item--active" : ""}`}
      aria-current={active ? "page" : undefined}
      data-testid={`${testIdPrefix}-${dasherize(item.label)}`}
      onClick={onNavigate}
      onMouseEnter={prefetch}
      onFocus={prefetch}
    >
      <span className="side-rail__icon">{renderIcon(item.icon, item.label)}</span>
      <span className="side-rail__label">{item.label}</span>
      {count > 0 && (
        <span
          className="side-rail__badge"
          data-testid={`${testIdPrefix}-badge-${dasherize(item.label)}`}
        >
          {count}
        </span>
      )}
    </Link>
  );
}

/**
 * One sortable row: the link itself stays an ordinary link, and the drag lives on
 * a separate grip button beside it. Dragging the link would mean choosing between
 * a drag and a click on every press; a grip keeps navigation untouched and gives
 * keyboard users a real control to reorder with.
 */
function SortableRailRow(props: RailLinkProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.item.href,
  });

  return (
    <li
      ref={setNodeRef}
      // eslint-disable-next-line sellwild-code/no-inline-styles -- dnd-kit drives the drag with a per-frame transform; there is no static class that can express it
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`side-rail__row${isDragging ? " side-rail__row--dragging" : ""}`}
    >
      <RailLink {...props} />
      <button
        type="button"
        className="side-rail__grip"
        aria-label={`Reorder ${props.item.label}`}
        data-testid={`${props.testIdPrefix}-grip-${dasherize(props.item.label)}`}
        {...attributes}
        {...listeners}
      >
        <GripIcon />
      </button>
    </li>
  );
}

export default function SideRail({
  pathname,
  items,
  basePath,
  name,
  testIdPrefix,
  renderIcon,
  badgeFor,
  cta,
  reorderKey,
}: SideRailProps) {
  const [open, setOpen] = useState(false);
  // null until the saved order has been read. The server has no localStorage, so
  // the first paint must be the shipped order or hydration mismatches.
  const [storedOrder, setStoredOrder] = useState<string[] | null>(null);

  // Close the mobile drawer whenever we navigate to a new page.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resets the locally-toggleable drawer in response to an external navigation event; not derivable during render because the user also opens/closes it directly
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!reorderKey) return;
    const saved = LS_Get<string[]>(reorderKey);
    if (Array.isArray(saved)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- localStorage is unavailable during SSR and the first client render, so the saved order can only be adopted after mount
      setStoredOrder(saved);
    }
  }, [reorderKey]);

  const ordered = useMemo(
    () => (reorderKey ? applyStoredOrder(items, storedOrder) : items),
    [items, storedOrder, reorderKey]
  );

  const sensors = useSensors(
    // A short distance/delay before a drag starts so a tap still reaches the grip
    // and a touch drag on the rail can still scroll it.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Only wired up inside the `reorderKey ?` branch below, so it takes the key
  // rather than re-checking it. A drop on nothing (`over` null) reorders
  // nothing, the same as a drop on an href this rail does not have.
  const handleDragEnd = (key: string, { active, over }: DragEndEvent) => {
    const hrefs = reorderForDrag(ordered, String(active.id), String(over?.id));
    if (!hrefs) return;
    setStoredOrder(hrefs);
    LS_Set(key, hrefs);
  };

  const list = (
    <ul className={`side-rail__list${reorderKey ? " side-rail__list--reorderable" : ""}`}>
      {cta && (
        <li>
          <Link
            href={cta.href}
            className="side-rail__item side-rail__item--cta"
            data-testid={`${testIdPrefix}-${dasherize(cta.label)}`}
            onClick={() => setOpen(false)}
          >
            <span className="side-rail__icon">{renderIcon(cta.icon, cta.label)}</span>
            <span className="side-rail__label">{cta.label}</span>
          </Link>
        </li>
      )}
      {ordered.map((b) => {
        const rowProps = {
          item: b,
          active: isActive(pathname, b.href, basePath),
          count: badgeFor ? badgeFor(b.href) : 0,
          testIdPrefix,
          renderIcon,
          onNavigate: () => setOpen(false),
        };
        return reorderKey ? (
          <SortableRailRow key={b.href} {...rowProps} />
        ) : (
          <li key={b.href}>
            <RailLink {...rowProps} />
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      {/* Mobile-only: hamburger toggle + dimming backdrop for the drawer. */}
      <button
        type="button"
        className="side-rail__hamburger same-color"
        aria-label={`Open ${name.toLowerCase()} menu`}
        aria-expanded={open}
        aria-controls={`${testIdPrefix}-nav`}
        data-testid={`${testIdPrefix}-toggle`}
        onClick={() => setOpen(true)}
      >
        <span className="side-rail__bars" aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      </button>
      <div
        className={`side-rail__backdrop${open ? " side-rail__backdrop--open" : ""}`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      <nav
        id={`${testIdPrefix}-nav`}
        className={`side-rail same-color${reorderKey ? " side-rail--reorderable" : ""}${open ? " side-rail--open" : ""}`}
        aria-label={name}
        data-testid={testIdPrefix}
      >
        <button
          type="button"
          className="side-rail__close"
          aria-label={`Close ${name.toLowerCase()} menu`}
          onClick={() => setOpen(false)}
        >
          &times;
        </button>
        {reorderKey ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={(event) => handleDragEnd(reorderKey, event)}
          >
            <SortableContext
              items={ordered.map((i) => i.href)}
              strategy={verticalListSortingStrategy}
            >
              {list}
            </SortableContext>
          </DndContext>
        ) : (
          list
        )}
      </nav>
    </>
  );
}
